import { ScrollArea, Stack } from "@mantine/core";
import { createContext, useContext, useState, type ReactNode } from "react";

import classes from "./PageColumns.module.css";

export interface PageColumnsProps {
  /** Above the scrolling body: tabs, a toolbar, a header. */
  top?: ReactNode;
  children: ReactNode;
  /** Below the body, or floating over it (the generation panel). */
  bottom?: ReactNode;
  /** False when the page scrolls something of its own. */
  scrollable?: boolean;
  /** Keeps the content clear of the vertical scrollbar, which otherwise covers its right edge. */
  offsetScrollbar?: boolean;
}

const PageScroll = createContext<HTMLDivElement | null>(null);

/**
 * The element the page's body scrolls in (`PageColumns`' own scroll), for a long list on the page
 * to follow (`VirtualMasonry`, `LoadAhead`); null until it mounts, and on a page that scrolls
 * something of its own.
 */
export function usePageScroll(): HTMLDivElement | null {
  return useContext(PageScroll);
}

/**
 * The page frame (design-system.md 3.8): 20px padding, the top content, then a body that fills
 * the rest of the page and scrolls by itself, then the bottom content. It is `position: relative`,
 * so a floating panel inside places itself against the page.
 */
export function PageColumns(props: PageColumnsProps) {
  const { top, children, bottom, scrollable = true, offsetScrollbar = false } = props;
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  return (
    // `data-page-frame` tells the shell this page pads itself (platform/shell/shell.module.css).
    <Stack gap={0} p={20} className={classes.page} data-page-frame>
      {top}
      {scrollable ? (
        <ScrollArea
          type="auto"
          offsetScrollbars={offsetScrollbar ? "present" : false}
          className={classes.body}
          classNames={{ content: classes.content }}
          viewportRef={setViewport}
        >
          <PageScroll value={viewport}>{children}</PageScroll>
        </ScrollArea>
      ) : (
        <div className={classes.body}>{children}</div>
      )}
      {bottom}
    </Stack>
  );
}
